import React from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import type { AttentionLevel, Task } from '@/src/renderer/lib/db/models'
import { TaskAttentionBadge } from '../TaskAttentionBadge'
import { TaskStartDateBadge } from '../TaskStartDateBadge'
import type { StartDateLabelMode } from '../TaskStartDateBadge'
import { TaskDropdown } from '../TaskDropdown'
import { AttentionDropdownContent } from '../AttentionDropdown'
import { WhenDropdownContent } from '../WhenDropdown'
import type { StartValue } from '../WhenDropdown'
import type { CollapsedDropdowns } from './hooks/useCollapsedDropdowns'

interface CollapsedTitleIconsProps {
  isExpanded: boolean
  selected: boolean
  effectiveAttention: AttentionLevel | null
  effectiveStartDate: Date | null
  startDiff: number | null
  collapsedStartValue: StartValue | null
  /** Task title, repeated as context when the date picker opens as a sheet. */
  taskTitle: string
  locale: string
  dropdowns: CollapsedDropdowns
  setAttention: (value: AttentionLevel | null) => void
  setStart: (value: StartValue | null) => void
  saveCollapsedToDb: (updates: Partial<Omit<Task, 'id'>>) => void
  selectOnDropdownOpen: (open: boolean) => void
  playSoftRemove: () => void
  startDateLabelMode?: StartDateLabelMode
  /** Abbreviated start-date label for narrow layouts (list window mode). */
  compact?: boolean
  /**
   * **„Ruhen lassen"** im Wann-Kasten der **Marke** (entschieden 2026-09-19).
   * Fehlt an einer Aufgabe, die schon ruht — dort gäbe es nichts zu legen.
   */
  onRest?: () => void
}

export const CollapsedTitleIcons: React.FC<CollapsedTitleIconsProps> = ({
  isExpanded, selected,
  effectiveAttention, effectiveStartDate, startDiff,
  collapsedStartValue, taskTitle, locale,
  dropdowns,
  setAttention, setStart, saveCollapsedToDb,
  selectOnDropdownOpen, playSoftRemove,
  startDateLabelMode, compact = false, onRest,
}) => (
  <AnimatePresence initial={false}>
    {!isExpanded && effectiveAttention && (
      <motion.span
        key="attention-icon"
        className="task-entry__title-icon"
        data-row-fit="title-icon"
        initial={{ width: 0, opacity: 0, marginRight: 0 }}
        animate={{ width: 20, opacity: 1, marginRight: 8 }}
        exit={{ width: 0, opacity: 0, marginRight: 0, scale: 0.7 }}
        transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
        onMouseEnter={() => dropdowns.switchTo('attention')}
      >
        <TaskDropdown
          open={dropdowns.attentionOpen}
          onOpenChange={(open) => { dropdowns.setAttentionOpen(open); selectOnDropdownOpen(open) }}
          side="bottom"
          align="start"
          alignOffset={-16}
          contentStyle={{ width: 220 }}
          trigger={<button className="task-entry__title-icon-btn task-entry__title-icon-btn--icon-only" type="button"><TaskAttentionBadge level={effectiveAttention} /></button>}
        >
          <AttentionDropdownContent
            value={effectiveAttention}
            onValueChange={(value) => { setAttention(value); saveCollapsedToDb({ attention: value }) }}
            onClose={() => dropdowns.setAttentionOpen(false)}
            onRemove={() => { playSoftRemove(); setAttention(null); saveCollapsedToDb({ attention: null }); dropdowns.setAttentionOpen(false) }}
            placement="badge"
          />
        </TaskDropdown>
      </motion.span>
    )}
    {!isExpanded && effectiveStartDate && startDiff !== null && (
      <motion.span
        key="start-day-icon"
        className="task-entry__title-icon"
        data-row-fit="title-icon"
        initial={{ width: 0, opacity: 0, marginRight: 0 }}
        animate={{ width: 'auto', opacity: 1, marginRight: 8 }}
        exit={{ width: 0, opacity: 0, marginRight: 0, scale: 0.7 }}
        transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
        onMouseEnter={() => dropdowns.switchTo('start')}
      >
        <TaskDropdown
          open={dropdowns.startOpen}
          onOpenChange={(open) => { dropdowns.setStartOpen(open); selectOnDropdownOpen(open) }}
          side="bottom"
          align="start"
          alignOffset={-16}
          dateSheet={{ title: taskTitle }}
          trigger={<button className={`task-entry__title-icon-btn${startDiff === 0 || startDiff === 1 ? ' task-entry__title-icon-btn--icon-only' : ''}${selected ? ' task-entry__title-icon-btn--selected' : ''}`} type="button"><TaskStartDateBadge date={effectiveStartDate} locale={locale} period={collapsedStartValue?.period ?? null} labelMode={startDateLabelMode} compact={compact} /></button>}
        >
          <WhenDropdownContent
            value={collapsedStartValue}
            onValueChange={(value) => {
              setStart(value)
              saveCollapsedToDb({ startDate: value?.date ?? null, startTime: value?.time ?? null, startPeriod: value?.period ?? null })
            }}
            onClose={() => dropdowns.setStartOpen(false)}
            onRemove={() => { playSoftRemove(); setStart(null); saveCollapsedToDb({ startDate: null, startTime: null, startPeriod: null }); dropdowns.setStartOpen(false) }}
            onRest={onRest}
            placement="badge"
          />
        </TaskDropdown>
      </motion.span>
    )}
  </AnimatePresence>
)
